import React, { JSX, useState } from "react";
import { Alert, Box, Tab, Tabs, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import writeXlsxFile from "write-excel-file";
import { ResourceCentreAutoComplete } from "../Subscriptions";
import { ResourceCentre } from "../../interfaces/ResourceCentreInterface";
import { Post } from "../../api/apiHelper";
import { useAppDispatch } from "../../store/hooks";
import { notificationAdd } from "../../actions/notification";
import { getRcData } from "../../api/resourceCentre";

enum TAB_ITEMS {
  DEMO_DATA = "demo-data",
  DOWNLOAD_RC_DATA = "download-rc-data"
}

const ImportDemoData = (): JSX.Element => {
  const [selectedRc, setSelectedRc] = useState<ResourceCentre | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = React.useState<string>("demo-data");

  const onSubmit = async () => {
    try {
      setHasErrors(false);
      setIsLoading(true);
      await Post("/api/addDemoData", { resourceCentreId: selectedRc?.id });
    } catch (e) {
      setHasErrors(true);
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "Error, data could not be imported.",
          autoTimeout: true
        })
      );
    } finally {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "success",
          message: "Data imported.",
          autoTimeout: true
        })
      );
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={activeTab} onChange={(_, tab) => setActiveTab(tab)}>
          <Tab label="Import Demo Data" value={TAB_ITEMS.DEMO_DATA} />
          <Tab label="Download RC Data" value={TAB_ITEMS.DOWNLOAD_RC_DATA} />
        </Tabs>
      </Box>
      {activeTab === TAB_ITEMS.DEMO_DATA && (
        <Box p={2}>
          <Typography gutterBottom>Quickly Add Demo Data To Selected Resourcecentres</Typography>
          <Typography variant="body2">
            This will add labtests, bills, products and assessment for demo purposes.
          </Typography>
          <Box mt={4} display="flex" flexDirection="column" gap={2} alignItems="flex-start">
            <ResourceCentreAutoComplete setSelectedRc={setSelectedRc} />
            <Alert severity="warning">
              Warning: Please make sure atleast one fiscal year is active before importing.
            </Alert>
            <LoadingButton
              variant="contained"
              disabled={!selectedRc}
              onClick={onSubmit}
              loading={isLoading}
            >
              Start Importing
            </LoadingButton>
          </Box>
          {hasErrors && (
            <Alert severity="error">
              Importing data failed but some data might have been imported.
            </Alert>
          )}
        </Box>
      )}
      {activeTab === TAB_ITEMS.DOWNLOAD_RC_DATA && (
        <Box p={2} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography gutterBottom>Download RC Data</Typography>
          <ResourceCentreAutoComplete setSelectedRc={setSelectedRc} />
          <Box display="flex" flexDirection="column" gap={2} alignItems="flex-start">
            <LoadingButton
              disabled={!selectedRc?.id}
              variant="contained"
              onClick={async () => {
                setIsDownloading(true);
                try {
                  const csvText = await getRcData(selectedRc!.id);

                  const sections = csvText.split(/\n### TABLE: /);
                  const sheets: { value: string }[][][] = [];
                  const sheetNames: string[] = [];

                  // eslint-disable-next-line no-restricted-syntax
                  for (const section of sections) {
                    // eslint-disable-next-line no-continue
                    if (!section.trim() || section.startsWith("EXPORT SUMMARY")) continue;

                    const lines = section.split("\n").filter((l) => l.trim());
                    const tableName = lines[0].trim();
                    const csvLines = lines.slice(1);

                    // eslint-disable-next-line no-continue
                    if (!csvLines.length) continue;

                    const rows = csvLines.map((line) =>
                      (
                        line.match(/(".*?"|[^",]+|(?<=,)(?=,)|^(?=,)|(?<=,)$)/g) ?? line.split(",")
                      ).map((cell) => ({ value: cell?.replace(/^"|"$/g, "").trim() ?? "" }))
                    );

                    sheets.push(rows);
                    sheetNames.push(tableName.slice(0, 31));
                  }

                  const summaryMatch = csvText.match(/### EXPORT SUMMARY\n([\s\S]+)$/);
                  if (summaryMatch) {
                    const summaryRows = summaryMatch[1]
                      .trim()
                      .split("\n")
                      .map((line) => line.split(",").map((cell) => ({ value: cell.trim() })));
                    sheets.push(summaryRows);
                    sheetNames.push("SUMMARY");
                  }

                  await writeXlsxFile(sheets, {
                    sheets: sheetNames,
                    fileName: `okhati_export_rcid_${selectedRc!.id}.xlsx`
                  });

                  dispatch(
                    notificationAdd({
                      id: new Date().getUTCMilliseconds(),
                      variant: "success",
                      message: "Data downloaded.",
                      autoTimeout: true
                    })
                  );
                } catch (e) {
                  dispatch(
                    notificationAdd({
                      id: new Date().getUTCMilliseconds(),
                      variant: "error",
                      message: "Error, data could not be downloaded.",
                      autoTimeout: true
                    })
                  );
                } finally {
                  setIsDownloading(false);
                }
              }}
              loading={isDownloading}
            >
              Download RC Data
            </LoadingButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ImportDemoData;
